/**
 * Turning what someone typed into an E.164 number Firebase will actually text.
 *
 * This is the last thing between a visitor and an SMS that never arrives, and
 * it used to be copied into three web modules that had drifted apart: the
 * execution surface — the one a visitor reaches from a QR code, on a phone,
 * with no account — had the weakest version of it.
 *
 * Brazil gets the detail because Brazil is the market. The rules here mirror
 * `functions/src/phoneNormalization/countryPlugins.js` so a number the client
 * accepts is the same `phoneE164` the server would compute, which is what
 * contact matching depends on.
 *
 * Pure: no network, no Firebase, no clock.
 */

const BR_DIAL_CODE = '+55';

/** Brazil is the primary market, so an unanswered geo lookup pre-fills +55. */
const DEFAULT_DIAL_CODE = BR_DIAL_CODE;

/**
 * Valid DDDs. A list rather than a range
 * because plenty of two-digit combinations are not area codes, and because
 * `55` *is* one (Rio Grande do Sul) — which is why a number cannot be
 * un-prefixed just by looking for a leading 55.
 */
const BR_AREA_CODES = Object.freeze([
  '11', '12', '13', '14', '15', '16', '17', '18', '19', '21', '22', '24', '27', '28',
  '31', '32', '33', '34', '35', '37', '38', '41', '42', '43', '44', '45', '46', '47',
  '48', '49', '51', '53', '54', '55', '61', '62', '63', '64', '65', '66', '67', '68',
  '69', '71', '73', '74', '75', '77', '79', '81', '82', '83', '84', '85', '86', '87',
  '88', '89', '91', '92', '93', '94', '95', '96', '97', '98', '99',
]);

function digitsOf(input) {
  return String(input == null ? '' : input).replace(/\D/g, '');
}

function isBrAreaCode(two) {
  return BR_AREA_CODES.includes(two);
}

/**
 * An 11-digit national mobile: DDD + 9 + eight digits. Every Brazilian mobile
 * has had the leading 9 since 2016.
 */
function isBrazilianMobile(digits) {
  const d = digitsOf(digits);
  return d.length === 11 && isBrAreaCode(d.slice(0, 2)) && d[2] === '9';
}

/**
 * The single most common way a Brazilian number is typed wrong: the old
 * eight-digit mobile, without the 9 that was added in 2016. `21 9000-0000`
 * instead of `21 99000-0000`.
 *
 * Only for mobiles. Old mobile ranges began 6–9 and landlines begin 2–5, so a
 * landline is left exactly as typed rather than silently turned into a mobile
 * that does not exist.
 */
function addMissingNinthDigit(nationalDigits) {
  const d = digitsOf(nationalDigits);
  if (d.length !== 10) return d;
  if (!isBrAreaCode(d.slice(0, 2))) return d;
  if (!/[6-9]/.test(d[2])) return d;
  return `${d.slice(0, 2)}9${d.slice(2)}`;
}

/**
 * Brazilian national digits, as the phone company would write them: trunk
 * prefix dropped, ninth digit restored.
 *
 * Returns null when the input is not recognisably a Brazilian national number,
 * so callers can tell "corrected" from "not ours".
 */
function brazilianNational(input) {
  let d = digitsOf(input);

  // Trunk prefix, from dialling it as a domestic long-distance call: 021 9…
  if (d.length >= 11 && d.startsWith('0')) d = d.replace(/^0+/, '');

  // Pasted with the country code. Checked before the bare-national case so a
  // `+55 55 9…` from Rio Grande do Sul still works: 13 digits with a valid DDD
  // behind the 55 can only be country-code-plus-national.
  if (d.startsWith('55') && d.length >= 12 && d.length <= 13 && isBrAreaCode(d.slice(2, 4))) {
    d = d.slice(2);
  }

  const withNinth = addMissingNinthDigit(d);
  if (withNinth.length === 11 && isBrAreaCode(withNinth.slice(0, 2)) && withNinth[2] === '9') {
    return withNinth;
  }
  // Landline: DDD + eight digits beginning 2–5. Valid, just not textable.
  if (d.length === 10 && isBrAreaCode(d.slice(0, 2))) return d;
  return null;
}

/**
 * What to send to Firebase.
 *
 * A number that reads as Brazilian is treated as Brazilian whatever the picker
 * says, because the picker is a guess made from an IP address and the number is
 * the thing the visitor actually knows.
 *
 * @param {string} dialCode e.g. '+55'
 * @param {string} local what was typed, punctuation and all
 * @returns {string}
 */
function toE164(dialCode, local) {
  const digits = digitsOf(local);
  const typedIntl = String(local || '').trim().startsWith('+');

  // An explicitly international number that is not ours goes through as typed.
  if (typedIntl && !digits.startsWith('55')) return `+${digits}`;

  const brazilian = brazilianNational(local);
  if (brazilian && (dialCode === BR_DIAL_CODE || typedIntl || isBrazilianMobile(brazilian))) {
    return `${BR_DIAL_CODE}${brazilian}`;
  }

  // NANP with its country code typed in: 1 555 123 4567. Keeping the 1 would
  // send +1 1 555…, which Firebase rejects.
  if (dialCode === '+1' && digits.length === 11 && digits.startsWith('1')) {
    return `+1${digits.slice(1)}`;
  }

  return `${dialCode}${digits}`;
}

/** Loose gate for the rest of the world: enough digits to be a phone number. */
function isPlausiblePhone(local) {
  const d = digitsOf(local);
  return d.length >= 7 && d.length <= 15;
}

/**
 * Why this number will not receive an SMS, or null when it should.
 *
 * Brazilian numbers are checked properly because a wrong one is silent: the
 * send succeeds, the code never arrives, and the visitor has no idea whether to
 * wait or retype. Everything else keeps the loose length check — guessing at
 * another country's numbering plan would reject valid numbers.
 *
 * @returns {'too_short'|'missing_area_code'|'landline'|'not_mobile'|null}
 */
function phoneInputProblem(dialCode, local) {
  const digits = digitsOf(local);
  if (!isPlausiblePhone(digits)) return 'too_short';

  const treatAsBr = dialCode === BR_DIAL_CODE || isBrazilianMobile(brazilianNational(local) || '');
  if (!treatAsBr) return null;

  const national = brazilianNational(local);
  if (!national) {
    // Nine digits starting with 9 is a mobile with the DDD left off — common,
    // and unfixable without knowing which city they are in.
    if (digits.length <= 9) return 'missing_area_code';
    return 'not_mobile';
  }
  if (national.length === 10) return 'landline';
  return null;
}

module.exports = {
  BR_DIAL_CODE,
  DEFAULT_DIAL_CODE,
  BR_AREA_CODES,
  digitsOf,
  isBrazilianMobile,
  addMissingNinthDigit,
  brazilianNational,
  toE164,
  isPlausiblePhone,
  phoneInputProblem,
};
